import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Container,
  Badge,
  UncontrolledTooltip,
  Col,
  Row,
  Button,
  Card,
} from "reactstrap";
import ReactBsTable from "components/Tables/ReactBsTable";
import DynamicFormModal from "components/Modal/Form";
import { tagFormFields } from "variables/formFields";
import { publisherDealData } from "components/Common/constants";
import { DEFAULT_STATUS_LABELS } from "variables/constants";
import { showAlert } from "components/Common/commonFunctions";
import moment from "moment";
import SearchableDropdown from "components/DropDown/SearchableDropdown";
import { DATE_RANGE_OPTIONS } from "variables/constants";
import TagContentModal from "components/Modal/TagContentModal";
import NotificationAlert from "react-notification-alert";
import { createTag, updateTag } from "actions/tag";
import { getTagList } from "actions/tag";
import { STATUS_OPTIONS } from "variables/constants";
import { getPublisherList } from "actions/publisher";
import { getDealList } from "actions/deal";
import { tagDetails } from "actions/tag";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const sortFieldMap = {
  publisher: "CompanyName",
  deal: "DealName",
  tagName: "TagName",
  version: "Version",
  lastUpdate: "UpdatedAt",
  createdOn: "CreatedAt",
};

const Tags = () => {
  const [data, setData] = useState({
    data: [],
    totalRecords: 0,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [selected, setSelected] = useState(null);
  const [showLoader, setshowLoader] = useState(false);
  const [tagContentModal, setTagContentModal] = useState(false);
  const [pageSize, setpageSize] = useState(50);
  const [sortConfig, setSortConfig] = useState({
    dataField: "",
    sortField: "",
    sortOrder: "",
  });
  const [formState, setFormState] = useState({
    values: {
      status: true,
    },
    errors: {},
  });
  const [dropDownData, setdropDownData] = useState({
    publisherData: {
      options: [],
      totalRecords: 0,
      searchString: "",
      pageNum: 1,
    },
    dealData: {
      options: [],
      totalRecords: 0,
      searchString: "",
      pageNum: 1,
    },
  });
  const notificationAlertRef = useRef(null);
  const isEditMode = !!selected;

  const [filters, setFilters] = useState({
    deal_name: "",
    company_name: "",
    tag_name: "",
    date_range: "",
    start_date: "",
    end_date: "",
    status: "",
    filterParams: [],
  });
  const [publisherOptions, setPublisherOptions] = useState([]);
  const [dealOptions, setDealOptions] = useState([]);

  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  //=== Tag List Api call
  const getTagListData = async () => {
    try {
      setshowLoader(true);
      const res = await getTagList(
        pageNum,
        filters.filterParams,
        "",
        pageSize,
        sortConfig.sortField,
        sortConfig.sortOrder,
      );
      if (res.code === 200) {
        setData({
          data: res?.data?.DealTag,
          totalRecords: res?.data?.totalRecords,
        });
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      console.error("Error fetching tag list:", error);
    } finally {
      setshowLoader(false);
    }
  };

  // COMBINED useEffect - removed separate sort useEffect
  useEffect(() => {
    getTagListData();
  }, [pageNum, filters.filterParams, pageSize, sortConfig.sortField, sortConfig.sortOrder]);

  const getPublisherOptions = async (searchString = "") => {
    try {
      const res = await getPublisherList(1, [], searchString);
      const options =
        res?.code === 200
          ? (res?.data?.Publisher || []).map((item) => ({
              label: item?.companyName || "",
              value: item?.ID || "",
            }))
          : [];
      setPublisherOptions(options);
    } catch (error) {
      console.error("Failed to load publisher options", error);
    }
  };

  const getDealOptions = async (searchString = "") => {
    try {
      const filterParams = [];
      if (filters.company_name) {
        filterParams.push({
          field: "PublisherID",
          condition: "eq",
          filterValues: [String(filters.company_name)],
        });
      }
      const res = await getDealList(
        0,
        JSON.stringify(filterParams),
        searchString,
      );
      const options =
        res?.code === 200
          ? (res?.data?.Deal || []).map((item) => ({
              label: item?.name || "",
              value: item?.name || "",
            }))
          : [];
      setDealOptions(options);
    } catch (error) {
      console.error("Failed to load deal options", error);
    }
  };

  useEffect(() => {
    getPublisherOptions();
    getDealOptions();
  }, []);

  useEffect(() => {
    getDealOptions();
  }, [filters.company_name]);

  const handlePublisherSearch = useCallback(({ query }) => {
    getPublisherOptions(query);
  }, []);

  const handleDealSearch = useCallback(({ query }) => {
    getDealOptions(query);
  }, []);

  //=== Tag List Api call

  const getDealOptionsByPublisher = (publisherValue) => {
    const value =
      publisherValue?.target?.value ?? publisherValue?.value ?? publisherValue;

    return publisherDealData.find((item) => item.value === value)?.deals || [];
  };

  //==> getDealsBypublisherId function is used for fetch deals by publisherId to show deals in dropdown.
  const getDealsBypublisherId = async (publisherId) => {
    try {
      const res = await getDealList(
        dropDownData.dealData.pageNum,
        JSON.stringify([
          {
            field: "PublisherID",
            condition: "eq",
            filterValues: [String(publisherId)],
          },
        ]),
        dropDownData.dealData.searchString,
      );
      const dealData =
        res?.code === 200
          ? (res?.data?.Deal || []).map((item) => ({
              label: item?.name || "",
              value: item?.ID,
            }))
          : [];
      setdropDownData((prev) => ({
        ...prev,
        dealData: {
          ...prev.dealData,
          options: dealData,
          totalRecords: res?.data?.totalRecords || 0,
        },
      }));
    } catch (error) {}
  };

  const handleChange = (e) => {
    const name = e?.target?.name || e?.name;
    const value = e?.target?.value ?? e?.value;

    setFormState((prev) => {
      const updatedValues = { ...prev.values, [name]: value };

      if (name === "publisher") {
        updatedValues.deal = "";
        getDealsBypublisherId(value);
      }

      return {
        values: updatedValues,
        errors: { ...prev.errors, [name]: "" },
      };
    });
  };

  const validateForm = () => {
    const errors = {};
    tagFormFields(
      dropDownData.publisherData.options,
      dropDownData.dealData.options,
    ).forEach((f) => {
      if (f.required && !formState.values[f.name]) {
        errors[f.name] = `${f.label} is required`;
      }
    });
    setFormState((p) => ({ ...p, errors }));
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setshowLoader(true);
    try {
      const payload = {
        publisherID: formState.values.publisher,
        dealID: formState.values.deal,
        name: formState.values.tagName,
        content: formState.values.content,
        status: formState.values.status ? "ACTIVE" : "INACTIVE",
      };
      const res = !isEditMode
        ? await createTag(payload)
        : await updateTag(selected?.ID, payload);
      if (res.code === 200) {
        notify("success", res?.message);
        getTagListData();
        setModalOpen(false);
        setSelected(null);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", "Something went wrong");
    } finally {
      setshowLoader(false);
    }
  };

  const confirmStatus = async (data) => {
    try {
      const payload = {
        publisherID: data.publisher.publisherID,
        dealID: data.deal.dealID,
        name: data.tagName,
        content: data.content,
        status: data.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
      };
      const res = await updateTag(data?.ID, payload);
      if (res.code === 200) {
        notify("success", res?.message);
        getTagListData();
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {}
  };

  const getTagDetailsById = async (row) => {
    try {
      const res = await tagDetails(row.ID);
      if (res.code === 200) {
        let data = res.data;
        setFormState({
          values: {
            tagName: data.tagName,
            content: data.content,
            publisher: data.publisher.publisherID,
            deal: data.deal.dealID,
            status: data.status === "ACTIVE",
          },
          errors: {},
        });
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {}
  };

  //==> handleEdit function is used for open modal in edit mode and patch the details.
  const handleEdit = (row) => {
    setSelected(row);
    getTagDetailsById(row);
    getDropDownData();
    getDealsBypublisherId(row.publisher.publisherID);
    // setFormState({
    //   values: {
    //     ...row,
    //     publisher: row.publisher.publisherID,
    //     deal: row.deal.dealID,
    //     status: row.status === "ACTIVE",
    //   },
    //   errors: {},
    // });

    setModalOpen(true);
  };

  const handleStatusToggle = async (row) => {
    const isActive = row.status === "ACTIVE";
    const res = await tagDetails(row?.ID);
    let data = res?.data;
    showAlert({
      title: `Are you sure you want to ${
        isActive ? "inactivate" : "activate"
      } this tag?`,
      message: `Tag "${row.tagName}" will be ${isActive ? "inactive" : "active"}.`,
      type: "warning",
      confirmStyle: "secondary",
      cancelStyle: "btn btn-primary btn-gradient-primary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: () => confirmStatus(data),
      onCancel: () => {},
    });
  };

  const columns = [
    {
      dataField: "publisher",
      text: "Publisher",
      formatter: (cell, row) => row?.publisher?.companyName || "-",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "deal",
      text: "Deal",
      formatter: (cell, row) => row?.deal?.dealName || "-",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "tagName",
      text: "Tag Name",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "version",
      text: "Version",
      formatter: (cell) => cell || "-",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "lastUpdate",
      text: "Last Updated",
      formatter: (cell) =>
        cell !== "" ? moment(cell).format("DD MMM YYYY") : "-",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "createdOn",
      text: "Created On",
      formatter: (cell) => moment(cell).format("DD MMM YYYY"),
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (cell) => (
        <Badge color={cell === "ACTIVE" ? "success" : "danger"}>{cell}</Badge>
      ),
    },
    {
      text: "Actions",
      dataField: "actions",
      formatter: (_, row) => {
        const rowId = row.ID;
        return (
          <>
            <a
              id={`copy${rowId}`}
              className="table-action table-action-info"
              onClick={() => {
                setSelected(row);
                setTagContentModal(true);
              }}
            >
              <i className="fas fa-tag" />
            </a>
            <UncontrolledTooltip target={`copy${rowId}`}>
              View Tag Content
            </UncontrolledTooltip>
            <a
              id={`edit${rowId}`}
              className="table-action table-action-info"
              onClick={() => {
                handleEdit(row);
              }}
            >
              <i className="fas fa-edit" />
            </a>
            <UncontrolledTooltip target={`edit${rowId}`}>
              Edit
            </UncontrolledTooltip>

            <a
              className={`table-action table-action-delete ${
                row.status === "ACTIVE" ? "status-active" : "status-inactive"
              }`}
              id={`toggle${rowId}`}
              onClick={(e) => {
                e.stopPropagation();
                handleStatusToggle(row);
              }}
            >
              {row.status === "ACTIVE" ? (
                <i className="fa-solid fa-xmark" />
              ) : (
                <i className="fa-solid fa-check" />
              )}
            </a>
            <UncontrolledTooltip target={`toggle${rowId}`}>
              {row.status === "ACTIVE" ? "Mark Inactive" : "Mark Active"}
            </UncontrolledTooltip>
          </>
        );
      },
    },
  ];

  const showCustomDateInputs = (name, value) => {
    const today = moment().format("YYYY-MM-DD");

    if (value === "Custom Range") {
      setFilters((prev) => ({
        ...prev,
        date_range: value,
        start_date: "",
        end_date: "",
      }));
      return;
    }

    let months = 0;
    if (value === "Last 1 Month") months = 1;
    if (value === "Last 3 Month") months = 3;
    if (value === "Last 6 Month") months = 6;
    if (value === "Last 12 Month") months = 12;

    const startDate = moment().subtract(months, "months").format("YYYY-MM-DD");

    setFilters((prev) => ({
      ...prev,
      date_range: value,
      start_date: startDate,
      end_date: today,
    }));
  };

  const buildFilterParams = () => {
    const filterParams = [];

    if (filters?.status !== "") {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filters?.status],
      });
    }

    if (filters?.deal_name !== "") {
      filterParams.push({
        field: "DealName",
        condition: "like",
        filterValues: [filters?.deal_name],
      });
    }
    if (filters?.company_name !== "") {
      filterParams.push({
        field: "PublisherID",
        condition: "in",
        filterValues: [String(filters?.company_name)],
      });
    }

    if (filters?.tag_name !== "") {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filters?.tag_name],
      });
    }

    if (filters?.start_date !== "" && filters?.end_date !== "") {
      filterParams.push({
        field: "CreatedAt",
        condition: "btw",
        filterValues: [filters?.start_date, filters?.end_date],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : "";
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      if (key === "start_date") {
        const startDate = value;
        const endDate = prev.end_date;

        if (endDate && new Date(startDate) <= new Date(endDate)) {
          return {
            ...prev,
            start_date: startDate,
          };
        }

        return {
          ...prev,
          start_date: startDate,
          end_date: "",
        };
      }

      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const handleSizePerPageChange = (e) => {
    const newSize = Number(e.target.value);
    setpageSize(newSize);
    setPageNum(1);
  };

  const handleSortChange = (dataField, sortOrder) => {
    const mappedSortField = sortFieldMap[dataField] || dataField;
    setSortConfig({ dataField, sortField: mappedSortField, sortOrder });
    // setPageNum(1);
  };

  const isAnyFilterApplied = (filters) => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === "filterParams") {
        return Array.isArray(value) && value.length > 0;
      }
      return value !== "" && value !== null && value !== undefined;
    });
  };

  const handleSearch = () => {
    if (!isAnyFilterApplied(filters)) return;
    const newfilterParams = buildFilterParams();

    setFilters({
      ...filters,
      filterParams: newfilterParams,
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleReset = () => {
    if (!isAnyFilterApplied(filters)) return;
    setFilters({
      deal_name: "",
      company_name: "",
      tag_name: "",
      date_range: "",
      start_date: "",
      end_date: "",
      status: "",
      filterParams: [],
    });
  };

  const filterSection = () => {
    return (
      <>
        <Row className="align-items-center">
          <Col md={2}>
            <SearchableDropdown
              name="company_name"
              options={publisherOptions}
              placeholder="Select Publisher"
              value={filters.company_name}
              onChange={(e) =>
                handleFilterChange("company_name", e.target.value)
              }
              onSearch={handlePublisherSearch}
              isSearchable={true}
            />
          </Col>
          <Col md={2}>
            <SearchableDropdown
              name="deal_name"
              options={dealOptions}
              placeholder="Deal Name"
              value={filters.deal_name}
              onChange={(e) => handleFilterChange("deal_name", e.target.value)}
              onSearch={handleDealSearch}
              isSearchable={true}
            />
          </Col>
          <Col md={2}>
            <input
              className="form-control"
              placeholder={"Tag Name"}
              value={filters.tag_name}
              onChange={(e) => handleFilterChange("tag_name", e.target.value)}
            />
          </Col>
          <Col md={2}>
            <SearchableDropdown
              options={STATUS_OPTIONS}
              placeholder="Status"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              isSearchable={false}
            />
          </Col>
        </Row>

        <Row className="mt-2">
          <>
            <Col md={2}>
              <SearchableDropdown
                options={DATE_RANGE_OPTIONS}
                placeholder="Date Range"
                value={filters.date_range}
                onChange={(e) =>
                  showCustomDateInputs("date_range", e.target.value)
                }
                isSearchable={false}
              />
            </Col>
            <Col md={2}>
              <div className="datepicker-wrapper">
                <DatePicker
                  selected={
                    filters.start_date ? new Date(filters.start_date) : null
                  }
                  onChange={(date) =>
                    handleFilterChange(
                      "start_date",
                      date ? formatDate(date) : "",
                    )
                  }
                  dateFormat="dd MMM yyyy"
                  placeholderText="Start Date"
                  className="form-control custom-datepicker"
                  disabled={filters.date_range !== "Custom Range"}
                />
                <i className="ni ni-calendar-grid-58 text-muted calendar-icon" />
              </div>
            </Col>

            <Col md={2}>
              <div className="datepicker-wrapper">
                <DatePicker
                  selected={
                    filters.end_date ? new Date(filters.end_date) : null
                  }
                  onChange={(date) =>
                    handleFilterChange("end_date", date ? formatDate(date) : "")
                  }
                  dateFormat="dd MMM yyyy"
                  placeholderText="End Date"
                  className="form-control custom-datepicker"
                  disabled={
                    filters.date_range !== "Custom Range" || !filters.start_date
                  }
                  minDate={
                    filters.start_date
                      ? new Date(filters.start_date)
                      : undefined
                  }
                />
                <i className="ni ni-calendar-grid-58 text-muted calendar-icon" />
              </div>
            </Col>
          </>
          <Col md={4}>
            <Button
              onClick={handleSearch}
              disabled={!isAnyFilterApplied(filters)}
              className="filter-btn-gradient-primary btn-md responsive"
            >
              Search
            </Button>

            <Button
              className="secondary"
              disabled={!isAnyFilterApplied(filters)}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </>
    );
  };

  const getDropDownData = async () => {
    try {
      const res = await getPublisherList();
      const publisherData =
        res?.code === 200
          ? (res?.data?.Publisher || []).map((item) => ({
              label: item?.companyName || "",
              value: item?.ID,
            }))
          : [];
      setdropDownData((prev) => ({
        ...prev,
        publisherData: {
          ...prev.publisherData,
          options: publisherData,
          totalRecords: res?.data?.totalRecords || 0,
        },
      }));
    } catch (error) {}
  };

  useEffect(() => {
    const { pageNum, totalRecords, searchString } = dropDownData.publisherData;

    const currentLength = dropDownData.publisherData.length;

    if (searchString) {
      getDropDownData();
      return;
    }

    if (pageNum > 1 && currentLength < totalRecords) {
      getDropDownData();
      return;
    }
  }, [
    dropDownData.publisherData.pageNum,
    dropDownData.publisherData.searchString,
  ]);

  //============================================

  //===================================================================

  const searchDropdownValues = (fieldName) => {
    switch (fieldName?.key) {
      case "publisher":
        setdropDownData((prev) => ({
          ...prev,
          publisherData: {
            ...prev.publisherData,
            searchString: fieldName?.query,
            pageNum: 1,
          },
        }));
        break;
      case "deal":
        setdropDownData((prev) => ({
          ...prev,
          dealData: {
            ...prev.dealData,
            searchString: fieldName?.query,
            pageNum: 1,
          },
        }));
        break;
      default:
        break;
    }
  };

  const onLoadMore = (fieldName) => {
    switch (fieldName) {
      case "publisher":
        if (dropDownData.publisherData.searchString !== "") return;
        setdropDownData((prev) => ({
          ...prev,
          publisherData: {
            ...prev.publisherData,
            pageNum: prev.publisherData.pageNum + 1,
          },
        }));
        break;
      case "deal":
        if (dropDownData?.dealData.searchString !== "") return;
        setdropDownData((prev) => ({
          ...prev,
          dealData: {
            ...prev.dealData,
            pageNum: prev.dealData.pageNum + 1,
          },
        }));
      default:
        break;
    }
  };

  //==> openModal function is used for open modal.
  const openModal = () => {
    setSelected(null);
    setFormState({
      values: { status: true },
      errors: {},
    });
    setdropDownData({
      publisherData: {
        options: [],
        totalRecords: 0,
        searchString: "",
        pageNum: 1,
      },
      dealData: {
        options: [],
        totalRecords: 0,
        searchString: "",
        pageNum: 1,
      },
    });
    getDropDownData();
    setModalOpen(true);
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container className="mt--6" fluid>
        <Card>
          <ReactBsTable
            title="Tags"
            description="Manage ad tags"
            handleSizePerPageChange={handleSizePerPageChange}
            data={data?.data}
            columns={columns}
            keyField="ID"
            addbtntext="Add Tag"
            onAddBtnClick={openModal}
            renderFilters={filterSection}
            page={pageNum}
            sizePerPage={pageSize}
            totalRecords={data?.totalRecords}
            enablePageSizeManagement={true}
            onPageChange={(p) => setPageNum(p)}
            onSortChange={handleSortChange}
            defaultSortField={sortConfig.dataField}
            defaultSortOrder={sortConfig.sortOrder}
            showLoader={showLoader}
          />
        </Card>
      </Container>

      <DynamicFormModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        title={isEditMode ? "Edit Tag" : "Add Tag"}
        // fields={tagFormFields.map((f) =>
        //   f.name === "deal"
        //     ? {
        //         ...f,
        //         options: getDealOptionsByPublisher(formState.values.publisher),
        //       }
        //     : f,
        // )}
        fields={tagFormFields(
          dropDownData.publisherData.options,
          dropDownData.dealData.options,
          isEditMode,
        )}
        formData={formState.values}
        errors={formState.errors}
        onChange={handleChange}
        onSubmit={handleSubmit}
        formState={formState}
        setFormState={setFormState}
        isEditMode={isEditMode}
        trueLabel={DEFAULT_STATUS_LABELS.active}
        falseLabel={DEFAULT_STATUS_LABELS.inactive}
        showLoader={showLoader}
      />
      <TagContentModal
        isTagContentModalOpen={tagContentModal}
        tagModalToggle={() => setTagContentModal(false)}
        modalHeader={"Tag Content Details"}
        label={"Tag Content"}
        textAreaValue={selected?.content}
      />
    </>
  );
};

export default Tags;